import { Button, LoadingState, Sidebar, unwrap, useApi } from '@remote-medium/ui';
import {
  Activity,
  Briefcase,
  Building2,
  LayoutDashboard,
  ListChecks,
  MessageSquarePlus,
  ShieldCheck,
  Tags,
  Users,
} from 'lucide-react';
import { Outlet, useNavigate } from 'react-router';

import { admin } from '../lib/client.ts';
import { authClient } from '../lib/auth.ts';
import { BillingEnabledContext } from '../lib/billing.tsx';
import { CurrentCuratorContext } from '../lib/current-curator.tsx';

// Layout + role guard. /admin/me answers 401 (no session) or 403 (not a
// curator); both send the visitor to the login page.
export function Shell() {
  const navigate = useNavigate();
  const { data: me, error, loading } = useApi(() => unwrap(admin.me.$get()), []);
  // Nav count pills: candidates waiting for screening, live roles.
  // Fetched once per shell mount; a failure just hides the pills.
  const counts = useApi(() => unwrap(admin['nav-counts'].$get()), []);

  if (loading) return <LoadingState fullPage />;
  if (error || !me?.curator) {
    // A pending invite or a deactivated account says so; any other 403 is
    // simply not a curator.
    const reason =
      error?.status !== 403
        ? undefined
        : error.code === 'curator_invite_pending' || error.code === 'curator_deactivated'
          ? error.message
          : 'This account is not a curator.';
    void navigate('/login', { replace: true, state: { reason } });
    return null;
  }

  async function signOut() {
    await authClient.signOut();
    void navigate('/login', { replace: true });
  }

  const pill = (n: number | undefined) => (n ? n : undefined);

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground md:h-screen md:flex-row">
      <Sidebar
        logoSrc="/assets/remote-medium-logo.svg"
        productLabel="Curator"
        groups={[
          {
            items: [
              { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
              {
                to: '/candidates',
                label: 'Candidates',
                icon: Users,
                badge: pill(counts.data?.newCandidates),
              },
              { to: '/companies', label: 'Companies', icon: Building2 },
              {
                to: '/roles',
                label: 'Roles',
                icon: Briefcase,
                badge: pill(counts.data?.liveRoles),
              },
            ],
          },
          {
            label: 'Matching',
            items: [
              { to: '/ranking', label: 'Ranking', icon: ListChecks },
              { to: '/taxonomy', label: 'Taxonomy', icon: Tags },
              { to: '/suggestions', label: 'Suggestions', icon: MessageSquarePlus },
            ],
          },
          {
            label: 'System',
            items: [
              { to: '/curators', label: 'Curators', icon: ShieldCheck },
              { to: '/ops', label: 'Ops', icon: Activity },
            ],
          },
        ]}
        footer={
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium">{me.curator.fullName}</span>
              <span title={me.user.email} className="truncate text-xs text-muted-foreground">
                {me.user.email}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={signOut}>
              Sign out
            </Button>
          </div>
        }
      />
      <main className="max-w-none flex-1 overflow-auto px-6 pt-10 pb-12">
        <div className="mx-auto max-w-[1152px]">
          <CurrentCuratorContext value={me.curator}>
            <BillingEnabledContext value={me.billingEnabled}>
              <Outlet />
            </BillingEnabledContext>
          </CurrentCuratorContext>
        </div>
      </main>
    </div>
  );
}
